import React, { useContext, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Upload, Camera, Loader2, CheckCircle2, RotateCcw, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * Upload File / Take Photo control for a single personnel document type.
 * - "Take Photo" uses the device camera on mobile (capture attribute).
 * - The selected file is shown for review BEFORE it is submitted.
 * - Explicit save states: Saving… → Saved / Failed — Retry.
 * Uploads reuse the existing /personnel/{id}/documents endpoint; the document
 * is saved as "Uploaded" and must be verified separately by a reviewer.
 */
export const DocumentCapture = ({ personnelId, docKey, label, onDone }) => {
  const { token } = useContext(AuthContext);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [pending, setPending] = useState(null); // { file, previewUrl, isImage }
  const [status, setStatus] = useState('idle'); // idle | uploading | saved | failed

  const pick = (ref) => ref.current?.click();

  const onSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File exceeds the 10MB limit'); return; }
    const isImage = (file.type || '').startsWith('image/');
    setPending({ file, previewUrl: isImage ? URL.createObjectURL(file) : null, isImage });
    setStatus('idle');
  };

  const cancel = () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  const confirm = async () => {
    const file = pending.file;
    setStatus('uploading');
    cancel();
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/personnel/${personnelId}/documents?doc_key=${docKey}`, fd,
        { headers: { Authorization: `Bearer ${token}` } });
      setStatus('saved');
      await onDone?.();
      setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 4000);
    } catch (err) {
      setStatus('failed');
      toast.error(err.response?.data?.detail || 'Upload failed — please retry');
    }
  };

  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,image/*" onChange={onSelect} data-testid={`pc-file-input-${docKey}`} />
      <input ref={camRef} type="file" className="hidden" accept="image/*" capture="environment" onChange={onSelect} data-testid={`pc-camera-input-${docKey}`} />

      {status === 'uploading' ? (
        <span className="inline-flex h-7 items-center gap-1 text-[11px] text-slate-500" data-testid={`pc-doc-saving-${docKey}`}><Loader2 size={12} className="animate-spin" />Saving…</span>
      ) : status === 'saved' ? (
        <span className="inline-flex h-7 items-center gap-1 text-[11px] font-semibold text-emerald-600" data-testid={`pc-doc-saved-${docKey}`}><CheckCircle2 size={12} />Saved</span>
      ) : status === 'failed' ? (
        <button onClick={() => pick(fileRef)} className="inline-flex h-7 items-center gap-1 rounded-md border border-rose-300 bg-white px-2.5 text-[11px] font-semibold text-rose-600 hover:bg-rose-50" data-testid={`pc-doc-retry-${docKey}`}><RotateCcw size={12} />Retry</button>
      ) : (
        <>
          <button onClick={() => pick(fileRef)} title="Upload a file" aria-label="Upload a file"
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-[11px] font-semibold text-[#0E2A47] transition-colors hover:border-[#1763B6] hover:bg-blue-50" data-testid={`pc-upload-${docKey}`}><Upload size={13} />File</button>
          <button onClick={() => pick(camRef)} title="Capture with camera" aria-label="Capture with camera"
            className="inline-flex h-7 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-[#0E2A47] transition-colors hover:border-[#1763B6] hover:bg-blue-50" data-testid={`pc-camera-${docKey}`}><Camera size={14} /></button>
        </>
      )}

      {pending && (
        <Dialog open onOpenChange={(o) => { if (!o) cancel(); }}>
          <DialogContent className="max-w-md" data-testid={`pc-doc-review-${docKey}`}>
            <DialogHeader>
              <DialogTitle>Review before saving — {label}</DialogTitle>
              <DialogDescription className="sr-only">Review the selected document before uploading it to the personnel record.</DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              {pending.isImage ? (
                <img src={pending.previewUrl} alt="Document preview" className="mx-auto max-h-72 rounded object-contain" data-testid={`pc-doc-preview-${docKey}`} />
              ) : (
                <div className="flex items-center gap-2 py-6 text-sm text-slate-600" data-testid={`pc-doc-preview-${docKey}`}><FileText size={20} className="text-[#1763B6]" />{pending.file.name}</div>
              )}
            </div>
            <p className="text-[11px] text-slate-400">Saved as “Uploaded” — an authorized reviewer must verify it before it is marked On File.</p>
            <DialogFooter>
              <button onClick={cancel} className="rounded-md border border-slate-200 px-4 py-2 text-sm" data-testid={`pc-doc-cancel-${docKey}`}>Cancel</button>
              <button onClick={confirm} className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#15375e]" data-testid={`pc-doc-confirm-${docKey}`}>Confirm &amp; Save</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </span>
  );
};

export default DocumentCapture;
